import type { Edge, Node } from '@xyflow/react';
import type { FlowNode } from '../../api/types';

/** Pure mapping between the API flow definition and the React Flow graph. */
export type GraphNode = Node<{ node: FlowNode }, 'autodm'>;

export const NODE_TYPES = [
  'trigger',
  'send_message',
  'ai_reply',
  'question',
  'condition',
  'delay',
  'tag',
  'untag',
  'randomizer',
  'keyword_router',
  'trigger_flow',
  'handoff',
  'note',
  'end',
] as const;
export const QUESTION_KINDS = [
  'name',
  'email',
  'phone',
  'text',
  'address',
  'yes_no',
  'number',
  'date',
  'rating',
] as const;

export function outgoingHandles(
  n: FlowNode,
): ReadonlyArray<{ handle: string; target: string | null }> {
  switch (n.type) {
    case 'trigger':
    case 'send_message':
    case 'question':
    case 'delay':
    case 'tag':
    case 'untag':
    case 'note':
    case 'ai_reply':
      return [{ handle: 'next', target: n.next }];
    case 'condition':
      return [
        { handle: 'yes', target: n.yes },
        { handle: 'no', target: n.no },
      ];
    case 'randomizer':
      return n.branches.map((b, i) => ({ handle: `branch-${i}`, target: b.next }));
    case 'keyword_router':
      // FLW-29
      return [
        ...n.branches.map((b, i) => ({ handle: `branch-${i}`, target: b.next })),
        { handle: 'otherwise', target: n.otherwise },
      ];
    default:
      return [];
  }
}

export function toGraph(
  nodes: readonly FlowNode[],
  positions: Readonly<Record<string, { x: number; y: number }>> = {},
): { nodes: GraphNode[]; edges: Edge[] } {
  const gnodes: GraphNode[] = nodes.map((n, i) => ({
    id: n.id,
    type: 'autodm',
    position: positions[n.id] ?? { x: 80 + (i % 3) * 260, y: 60 + Math.floor(i / 3) * 170 },
    data: { node: n },
  }));
  const edges: Edge[] = nodes.flatMap((n) =>
    outgoingHandles(n)
      .filter((h) => h.target !== null)
      .map((h) => ({
        id: `${n.id}:${h.handle}`,
        source: n.id,
        sourceHandle: h.handle,
        target: h.target as string,
        label: h.handle === 'next' ? undefined : h.handle,
      })),
  );
  return { nodes: gnodes, edges };
}

/** Writes the edges back into the node definitions; a node keeps its other fields. */
export function applyEdges(nodes: readonly FlowNode[], edges: readonly Edge[]): FlowNode[] {
  const targetOf = (id: string, handle: string): string | null =>
    edges.find((e) => e.source === id && (e.sourceHandle ?? 'next') === handle)?.target ?? null;
  return nodes.map((n) => {
    switch (n.type) {
      case 'trigger':
      case 'send_message':
      case 'question':
      case 'delay':
      case 'tag':
      case 'untag':
      case 'note':
      case 'ai_reply':
        return { ...n, next: targetOf(n.id, 'next') };
      case 'condition':
        return { ...n, yes: targetOf(n.id, 'yes'), no: targetOf(n.id, 'no') };
      case 'randomizer':
        return {
          ...n,
          branches: n.branches.map((b, i) => ({ ...b, next: targetOf(n.id, `branch-${i}`) })),
        };
      case 'keyword_router':
        return {
          ...n,
          branches: n.branches.map((b, i) => ({ ...b, next: targetOf(n.id, `branch-${i}`) })),
          otherwise: targetOf(n.id, 'otherwise'),
        };
      default:
        return n;
    }
  });
}

export const nextId = (type: string): string => `${type}_${crypto.randomUUID().slice(0, 8)}`;

/** UI-31: the PRD's "Follower Only" component is a condition on the contact's follower flag. */
export function followerOnlyNode(): FlowNode {
  return {
    id: nextId('condition'),
    type: 'condition',
    field: 'contact.is_follower',
    op: 'equals',
    value: 'true',
    yes: null,
    no: null,
  };
}

export function newNode(type: (typeof NODE_TYPES)[number]): FlowNode {
  const id = nextId(type);
  switch (type) {
    case 'trigger':
      return { id, type, on: 'comment', next: null };
    case 'send_message':
      return { id, type, message: { kind: 'text', text: 'Hello {{first_name}}!' }, next: null };
    case 'ai_reply':
      return {
        id,
        type,
        instruction: 'Answer the question helpfully',
        fallback: 'Thanks, we will get back to you.',
        next: null,
      };
    case 'question':
      return {
        id,
        type,
        kind: 'email',
        prompt: 'What is your email?',
        retryPrompt: 'Please send a valid email.',
        saveAs: 'email',
        next: null,
      };
    case 'condition':
      return {
        id,
        type,
        field: 'contact.is_follower',
        op: 'equals',
        value: 'true',
        yes: null,
        no: null,
      };
    case 'delay':
      return { id, type, seconds: 3600, next: null };
    case 'tag':
      return { id, type, tag: 'lead', next: null };
    case 'untag':
      // FLW-25
      return { id, type, tag: 'lead', next: null };
    case 'randomizer':
      return {
        id,
        type,
        branches: [
          { weight: 50, next: null },
          { weight: 50, next: null },
        ],
      };
    case 'keyword_router':
      // FLW-29
      return {
        id,
        type,
        branches: [
          { keywords: ['price'], next: null },
          { keywords: ['size'], next: null },
        ],
        otherwise: null,
      };
    case 'trigger_flow':
      return { id, type, flowId: '' };
    case 'handoff':
      return { id, type, message: 'A team member will reach out shortly.' };
    case 'note':
      return { id, type, text: 'Note', next: null };
    case 'end':
      return { id, type };
  }
}

export const MESSAGE_KINDS = ['text', 'button', 'image', 'video', 'file'] as const;
type SendMessage = Extract<FlowNode, { type: 'send_message' }>['message'];

/** FLW-19 / AUT-20: the editor's format picker; a PDF is a file message, as the wizard sends it. */
export function messageForKind(kind: string): SendMessage {
  switch (kind) {
    case 'button':
      return { kind: 'button', text: '', buttons: [{ title: 'Open', url: 'https://' }] };
    case 'image':
      return { kind: 'image', url: 'https://' };
    case 'video':
      // AUT-19
      return { kind: 'video', url: 'https://' };
    case 'file':
      return { kind: 'file', url: 'https://' };
    default:
      return { kind: 'text', text: '' };
  }
}

/** UI-35: what the Format picker should show for a message; a PDF is a single "Download PDF" button. */
export function formatOf(m: SendMessage): (typeof MESSAGE_KINDS)[number] | 'ai_text' {
  return m.kind;
}

export function summarize(n: FlowNode): string {
  switch (n.type) {
    case 'trigger':
      return `on ${n.on.replace('_', ' ')}`;
    case 'send_message':
      return n.message.kind === 'text'
        ? n.message.text
        : n.message.kind === 'button'
          ? `${n.message.text} [${n.message.buttons.map((b) => b.title).join(', ')}]`
          : n.message.kind === 'image' || n.message.kind === 'video'
            ? `${n.message.kind}: ${n.message.url}`
            : n.message.kind === 'file'
              ? `pdf: ${n.message.url}`
              : `AI: ${n.message.instruction}`;
    case 'ai_reply':
      return `AI: ${n.instruction}`;
    case 'question':
      return `${n.kind} → ${n.saveAs}: ${n.prompt}`;
    case 'condition':
      return `${n.field} ${n.op} ${n.value ?? ''}`;
    case 'delay':
      return `wait ${n.seconds >= 3600 ? `${Math.round(n.seconds / 3600)} h` : `${n.seconds} s`}`;
    case 'tag':
      return `tag ${n.tag}`;
    case 'untag':
      return `untag ${n.tag}`;
    case 'randomizer':
      return n.branches.map((b) => `${b.weight}%`).join(' / ');
    case 'keyword_router':
      return `route: ${n.branches.map((b) => b.keywords.join(', ')).join(' / ')}`;
    case 'trigger_flow':
      return `start flow ${n.flowId || '?'}`;
    case 'handoff':
      return n.message;
    case 'note':
      return n.text;
    case 'end':
      return 'end';
  }
}
